<template>
  <OrganizationChartSkeleton v-if="loading" />
  <OrgChart
    v-else
    :key="chartKey"
    ref="orgChartRef"
    :data 
    :minimap="false"
  >
    <template #node="{ item, children, childrenExpanded, toggleChildren }">
      <OrganizationChartNode
        :visible-fields
        :item
        :children
        :children-expanded
        :toggle-children
        :value-formatter
        :amount-formatter
      />
    </template>
    <template #noData>
      <UILayoutNoData hide-question-icon inline />
    </template>
  </OrgChart>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watchEffect } from 'vue'
import { omit, orderBy } from 'lodash'
import { OrgChart } from '@moonwane/vue3-org-chart'
import '@moonwane/vue3-org-chart/dist/vue3-org-chart.css'

import { AnalyticsTree } from '@types'

import { ORGANIZATION_CHART_FIELDS } from './utils/const'

import OrganizationChartNode from './components/OrganizationChartNode.vue'
import OrganizationChartSkeleton from './OrganizationChartSkeleton.vue'
import { UILayoutNoData } from '@ui/core'

type Props = {
  data: AnalyticsTree[]
  loading?: boolean
  hiddenFields?: string[]
  valueFormatter?: (value: number) => string
  amountFormatter?: (value: number) => string
}

type Emits = {
  'ready': [instance: typeof OrgChart.prototype]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ inheritAttrs: false })

const orgChartRef = useTemplateRef('orgChartRef')

const visibleFields = computed(() => ORGANIZATION_CHART_FIELDS?.filter(field => !props.hiddenFields?.includes(field)))

const data = computed(() => {
  return orderBy(props.data?.map(item => {
    const id = item.path.join('-')
    const parentId = item.path.slice(0, -1).join('-') || ''
    const assetId = item.path.at(-1)
    return {
      ...omit(item, 'path'),
      id,
      parentId,
      assetId,
    }
  }) || [], 'balance', 'desc')
})

const chartKey = computed(() => JSON.stringify(data.value))

watchEffect(() => {
  if (!orgChartRef.value) return
  emit('ready', orgChartRef.value)
})
</script>
